import { ArrayBufferTarget, Muxer } from 'mp4-muxer';
import { createFmp4Splitter, parseInit, type InitTrackInfo } from './fmp4-split';
import type { MuxerAdapter, MuxerConfig } from './muxer-adapter';

interface Sample { bytes: Uint8Array; timestamp: number; duration: number; key: boolean }
interface Part { samples: Sample[]; end: number }
const MAX_QUEUE_BYTES = 16 * 1024 * 1024;
const MAX_PART_BYTES = 8 * 1024 * 1024;
const MAX_QUEUE_US = 2_000_000;

function concat(...pieces: Uint8Array[]): Uint8Array {
  const bytes = new Uint8Array(pieces.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const piece of pieces) { bytes.set(piece, offset); offset += piece.length; }
  return bytes;
}
function u32(value: number): Uint8Array {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) throw new Error('fMP4 integer out of range');
  const bytes = new Uint8Array(4); new DataView(bytes.buffer).setUint32(0, value); return bytes;
}
function u64(value: number): Uint8Array {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('fMP4 decode time out of range');
  return concat(u32(Math.floor(value / 2 ** 32)), u32(value % 2 ** 32));
}
function box(type: string, ...pieces: Uint8Array[]): Uint8Array {
  return concat(u32(8 + pieces.reduce((n, p) => n + p.length, 0)), Uint8Array.from(type, c => c.charCodeAt(0)), ...pieces);
}
function full(type: string, version: number, flags: number, ...pieces: Uint8Array[]): Uint8Array {
  return box(type, new Uint8Array([version, flags >> 16, flags >> 8, flags]), ...pieces);
}

/**
 * Public mp4-muxer APIs generate the codec initialization from the first real samples.
 * Media fragments are written here so a delta sample can start a transport part without
 * being mislabelled a keyframe. Timestamps are decode ordered (realtime encoding, no B frames).
 */
export class PartsMuxerAdapter implements MuxerAdapter {
  onData: ((bytes: Uint8Array) => void) | null = null;
  private config: MuxerConfig | null = null;
  private tracks: InitTrackInfo[] = [];
  private firstVideo: { sample: Sample; meta?: EncodedVideoChunkMetadata } | null = null;
  private firstAudio: { sample: Sample; meta?: EncodedAudioChunkMetadata } | null = null;
  private video: Sample[] = [];
  private audio: Sample[] = [];
  private pending: Part[] = [];
  private nextCut = 0;
  private videoTime = -1;
  private audioTime = -1;
  private audioEnd = -1;
  private queuedBytes = 0;
  private sequence = 1;
  private finished = false;

  constructor(private readonly partMs: number = 200) {
    if (partMs !== 200) throw new Error('Unsupported transport part duration');
  }
  configure(config: MuxerConfig): void {
    if (this.config) throw new Error('Parts muxer already configured');
    if (!config.video && !config.audio) throw new Error("At least one track is required");
    this.config = config;
  }
  addVideo(chunk: EncodedVideoChunk, meta: EncodedVideoChunkMetadata | undefined, timestampUs: number, isSegmentStart: boolean, durationUs?: number): void {
    if (!this.requireConfig().video) throw new Error("Video track not configured");
    if (timestampUs <= this.videoTime) throw new Error('Video timestamps must be in decode order');
    if (!this.firstVideo && (timestampUs !== 0 || chunk.type !== 'key')) throw new Error('Parts must begin with a keyframe at zero');
    if (isSegmentStart && chunk.type !== 'key') throw new Error('GOP start is not a keyframe');
    const sample = this.sample(chunk, timestampUs, durationUs ?? chunk.duration ?? 0);
    if (this.video.length && (timestampUs >= this.nextCut || isSegmentStart)) {
      this.pending.push({ samples: this.video, end: timestampUs }); this.video = [];
      while (this.nextCut <= timestampUs) this.nextCut += this.partMs * 1000;
    }
    if (!this.firstVideo) {
      this.firstVideo = { sample, meta }; this.nextCut = timestampUs + this.partMs * 1000;
    }
    this.video.push(sample); this.videoTime = timestampUs;
    this.flush(false); this.checkBudget();
  }
  addAudio(chunk: EncodedAudioChunk, meta: EncodedAudioChunkMetadata | undefined, timestampUs: number): void {
    const config = this.requireConfig();
    if (!config.audio) throw new Error('Audio track not configured');
    if (!config.video && !this.firstAudio && (!meta?.decoderConfig || meta.decoderConfig.codec !== 'mp4a.40.2' || meta.decoderConfig.sampleRate !== config.audio.sampleRate || meta.decoderConfig.numberOfChannels !== config.audio.numberOfChannels)) throw new Error('Actual AAC decoder metadata required');
    if (timestampUs <= this.audioTime) throw new Error('Audio timestamps must be in decode order');
    const duration = chunk.duration || (config.audio.codec === 'aac' ? 1024e6 / config.audio.sampleRate : 0);
    const sample = this.sample(chunk, timestampUs, duration);
    if (!config.video && this.audio.length && timestampUs >= this.nextCut) {
      if (this.initialize(false)) this.emit([], this.audio.splice(0));
      while (this.nextCut <= timestampUs) this.nextCut += this.partMs * 1000;
    }
    if (!this.firstAudio) {
      if (!config.video && timestampUs !== 0) throw new Error('Audio parts must begin at zero');
      this.firstAudio = { sample, meta };
      if (!config.video) this.nextCut = this.partMs * 1000;
    }
    this.audio.push(sample); this.audioTime = timestampUs; this.audioEnd = timestampUs + duration;
    this.flush(false); this.checkBudget();
  }
  finalize(): void {
    if (this.finished) return;
    this.requireConfig();
    if (this.video.length) {
      const last = this.video[this.video.length - 1]!;
      this.pending.push({ samples: this.video, end: last.timestamp + last.duration }); this.video = [];
    }
    this.flush(true);
    if (!this.config!.video && this.audio.length && this.initialize(true)) this.emit([], this.audio.splice(0));
    if (this.pending.length) throw new Error('Missing codec metadata or audio at finalization');
    this.audio = []; this.firstVideo = null; this.firstAudio = null; this.queuedBytes = 0; this.finished = true;
  }
  /** Revocation discards retained media without a final fragment. */
  discard(): void {
    this.finished = true; this.onData = null; this.config = null; this.tracks = [];
    this.firstVideo = null; this.firstAudio = null; this.video = []; this.audio = []; this.pending = []; this.queuedBytes = 0;
  }
  private requireConfig(): MuxerConfig {
    if (!this.config || this.finished) throw new Error('Parts muxer is not active');
    return this.config;
  }
  private sample(chunk: EncodedVideoChunk | EncodedAudioChunk, timestamp: number, duration: number): Sample {
    if (!Number.isFinite(timestamp) || timestamp < 0 || !Number.isFinite(duration) || duration <= 0) throw new Error('Invalid sample timestamp or duration');
    if (chunk.byteLength <= 0 || chunk.byteLength > MAX_PART_BYTES || this.queuedBytes + chunk.byteLength > MAX_QUEUE_BYTES) throw new Error('Encoded sample exceeds queue budget');
    const bytes = new Uint8Array(chunk.byteLength); chunk.copyTo(bytes); this.queuedBytes += bytes.length;
    return { bytes, timestamp, duration, key: chunk.type === 'key' };
  }
  private initialize(final: boolean): boolean {
    if (this.tracks.length) return true;
    const video = this.firstVideo, audio = this.firstAudio, config = this.requireConfig();
    if ((config.video && !video) || (config.audio && !audio && (!config.video || !final || config.audio.codec !== 'aac'))) return false;
    const target = new ArrayBufferTarget();
    const muxer = new Muxer({ target,
      ...(config.video ? { video: { codec: 'avc' as const, ...config.video } } : {}),
      ...(config.audio ? { audio: config.audio } : {}), fastStart: 'fragmented', firstTimestampBehavior: 'cross-track-offset' });
    if (video) muxer.addVideoChunkRaw(video.sample.bytes, 'key', 0, video.sample.duration, video.meta);
    if (audio) muxer.addAudioChunkRaw(audio.sample.bytes, 'key', audio.sample.timestamp, audio.sample.duration, audio.meta);
    else if (config.audio) {
      // A retired video generation may end before the first eligible AAC packet.
      // mp4-muxer derives AAC-LC initialization from rate/channels, but omits an
      // unsampled track. Seed only this temporary init builder with an empty
      // sample; its media fragments are discarded below. Published fragments
      // contain only real packets, so the next generation keeps all its audio.
      muxer.addAudioChunkRaw(new Uint8Array(0), 'key', 0, 1024e6 / config.audio.sampleRate);
    }
    muxer.finalize();
    let init: Uint8Array | undefined;
    createFmp4Splitter({ onInit: bytes => { init = bytes; }, onFragment() {} }).push(new Uint8Array(target.buffer));
    if (!init) throw new Error('Codec initialization missing');
    this.tracks = parseInit(init);
    if ((config.video && !this.tracks.some(t => t.handler === 'vide')) || (config.audio && !this.tracks.some(t => t.handler === 'soun'))) throw new Error('Codec initialization lacks a configured track');
    this.onData?.(init);
    return true;
  }
  private flush(final: boolean): void {
    if (!this.initialize(final)) return;
    while (this.pending.length) {
      const part = this.pending[0]!;
      if (!final && this.config!.audio && this.audioEnd < part.end) break;
      const audioCount = final && this.pending.length === 1 ? this.audio.length : this.audio.findIndex(s => s.timestamp >= part.end);
      const sound = this.audio.splice(0, audioCount < 0 ? this.audio.length : audioCount);
      this.pending.shift();
      this.emit(part.samples, sound);
    }
  }
  private emit(video: Sample[], audio: Sample[]): void {
    const tracks = this.tracks.map(info => ({ info, samples: info.handler === 'vide' ? video : audio })).filter(t => t.samples.length);
    const payload = tracks.flatMap(t => t.samples.map(s => s.bytes));
    const payloadSize = payload.reduce((n, bytes) => n + bytes.length, 0);
    if (payloadSize > MAX_PART_BYTES) throw new Error('Transport part exceeds byte budget');
    const traf = (info: InitTrackInfo, samples: Sample[], dataOffset: number) => {
      const ticks = (us: number) => Math.round(us * info.timescale / 1e6);
      return box('traf', full('tfhd', 0, 0x020000, u32(info.trackId)),
        full('tfdt', 1, 0, u64(ticks(samples[0]!.timestamp))),
        full('trun', 0, 0x701, u32(samples.length), u32(dataOffset), ...samples.map(sample => {
          const duration = ticks(sample.timestamp + sample.duration) - ticks(sample.timestamp);
          if (duration <= 0) throw new Error('Zero duration after timescale conversion');
          return concat(u32(duration), u32(sample.bytes.length), u32(sample.key ? 0x02000000 : 0x01010000));
        })));
    };
    const header = full('mfhd', 0, 0, u32(this.sequence++));
    const size = box('moof', header, ...tracks.map(t => traf(t.info, t.samples, 0))).length;
    let offset = size + 8;
    const moof = box('moof', header, ...tracks.map(t => {
      const bytes = traf(t.info, t.samples, offset);
      offset += t.samples.reduce((n, s) => n + s.bytes.length, 0); return bytes;
    }));
    this.onData?.(concat(moof, box('mdat', ...payload)));
    this.queuedBytes -= payloadSize;
  }
  private checkBudget(): void {
    const oldest = this.pending[0]?.samples[0]?.timestamp ?? this.video[0]?.timestamp ?? this.audio[0]?.timestamp;
    if (oldest !== undefined && Math.max(this.videoTime, this.audioTime) - oldest > MAX_QUEUE_US) throw new Error('Audio/video queue exceeded two seconds');
  }
}
